import {
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  IconButton,
  Input,
  InputGroup,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Text,
  Tooltip,
  useBoolean,
} from '@chakra-ui/react';
import { ReactComponent as LinkIcon } from 'assets/icons/link.svg';
import { ChangeEvent, FormEvent, MouseEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isValidUrl, withProtocol } from 'utils/url';

import { isVideoLink } from './VideoEmbedWidget.helper';

interface ToggleProps {
  isActive: boolean;
  value?: string;
  // Null is when the alert is dismissed by clicking outside
  // Or when an invalid url is entered
  onChange: (url: string | null) => void;
  onMouseDown?: (event: MouseEvent<HTMLButtonElement>) => void;
}

export const VideoEmbedWidgetToolbar = ({ value, isActive, onChange, onMouseDown }: ToggleProps) => {
  const { t } = useTranslation('editor_widget_menu', { useSuspense: false, keyPrefix: 'sideMenu.videoEmbedMenu' });
  const label = t('inputLabel');
  const [isOpen, setIsOpen] = useBoolean(false);
  const [hasLinkError, setHasLinkError] = useBoolean(false);
  const [link, setLink] = useState(value || '');

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const fullUrl = withProtocol(link);

    // unsupported video
    if (!isVideoLink(fullUrl)) {
      setHasLinkError.on();
      return;
    }

    // close popover
    setIsOpen.off();

    if (isValidUrl(fullUrl)) {
      return onChange(fullUrl);
    }

    // invalid url
    return onChange(null);
  };

  const onChangeLinkEventHandler = (e: ChangeEvent<HTMLInputElement>) => {
    if (hasLinkError) setHasLinkError.off();
    setLink(e.target.value);
  };

  return (
    <Popover isLazy placement='bottom-start' isOpen={isOpen} onClose={setIsOpen.off}>
      <Tooltip hasArrow placement='bottom' label={label} bg='black'>
        <Box>
          <PopoverTrigger>
            <IconButton
              isActive={isActive}
              onMouseDown={onMouseDown}
              onClick={setIsOpen.toggle}
              size='sm'
              aria-label={label}
              variant='icon-btn-toolbar'
              icon={<LinkIcon />}
              data-testid='add-link-trigger'
            />
          </PopoverTrigger>
        </Box>
      </Tooltip>
      <PopoverContent>
        <form onSubmit={onSubmit}>
          <Flex direction='column' p={4} gap={4}>
            <FormControl isInvalid={hasLinkError}>
              <Flex direction='column' gap={2} position='relative'>
                <FormLabel fontSize='sm' htmlFor='toolbar-link-input' fontWeight='bold' mb={0}>
                  {t('inputLabel')}
                </FormLabel>
                <Text as='p' fontSize='sm' fontWeight='normal'>
                  {t('supportedVideos')}
                </Text>
                <InputGroup>
                  <Input
                    id='toolbar-link-input'
                    size='sm'
                    border='1px solid'
                    borderColor='gray.300'
                    value={link}
                    onChange={onChangeLinkEventHandler}
                    data-testid='toolbar-link-input'
                  />
                </InputGroup>
                <FormErrorMessage fontSize='xs'>{t('enterValidURL')}</FormErrorMessage>
              </Flex>
              <Flex justify='flex-end'>
                <Button type='submit' size='sm' mt={2} variant='action' data-testid='toolbar-link-submit-btn'>
                  {t('submitButton')}
                </Button>
              </Flex>
            </FormControl>
          </Flex>
        </form>
      </PopoverContent>
    </Popover>
  );
};

VideoEmbedWidgetToolbar.displayName = 'VideoEmbedWidgetToolbar';
